/**
 * SystemMenuDrawer.js
 * "The Gemini Bar" - Main system navigation drawer.
 * Adaptive: Mobile (Overlay Drawer) / Desktop (Expandable Sidebar)
 */
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import { app } from "../../firebase-config.js";
import { DRAWER_MENU, BOTTOM_TABS, SIDEBAR_STRUCTURE } from '../../config/navigation.js';
import { renderIcon } from '../../utils/icons.js';
import { APP_VERSION } from '../../version.js';
import { logout, waitForAuthReady } from '../../auth.js';
import { ModalManager } from '../../utils/ModalManager.js';
import { InfoModal } from '../modals/InfoModal.js';
import { ReportModal } from '../modals/ReportModal.js';
import { ProposalModal } from '../modals/ProposalModal.js';
import { GuideModal } from '../modals/GuideModal.js';

const db = getFirestore(app);

export class SystemMenuDrawer {
    constructor(container) {
        this.container = container;
        this.isOpen = false; // Mobile state
        this.isDesktopExpanded = false; // Desktop state
        this.activePath = window.location.pathname; // Track active path

        // Listen for navigation events to update active state
        window.addEventListener('popstate', () => this.updateActiveState());

        // [FIX] Sidebar Disappearance on Refresh
        // Wait for Auth to be ready, then re-render menu to ensure correct Role (Author/Admin)
        waitForAuthReady.then(user => {
            console.log("[SystemMenuDrawer] Auth Ready. Refreshing Menu for:", user ? user.email : "Guest");
            this.refreshMenu();
            this.updateHeader();
        });

        // [FIX] Listen for Profile Updates (SPA Sync)
        window.addEventListener('user-profile-update', (e) => {
            console.log("[SystemMenuDrawer] Profile Updated via Event:", e.detail.user);

            // 1. Force Sync Global State (Defensive)
            if (e.detail.user && window.user) {
                Object.assign(window.user, e.detail.user);
            }

            // 2. Update UI (Header First - Priority)
            try {
                this.updateHeader();
            } catch (err) {
                console.warn("[SystemMenuDrawer] Failed to update header:", err);
            }

            // 3. Refresh Menu Items
            try {
                this.refreshMenu();
            } catch (err) {
                console.warn("[SystemMenuDrawer] Failed to refresh menu:", err);
            }
        });

        this.render();
    }

    updateActiveState() {
        this.activePath = window.location.pathname;
        if (this.refreshMenu) this.refreshMenu();
        // [FIX] Update Drawer Layout (Top/Padding) when route changes (e.g. entering/leaving viewer)
        if (this.updateDrawerState) this.updateDrawerState();
    }

    render() {
        // Overlay (Mobile & Desktop)
        this.overlay = document.createElement('div');
        this.overlay.className = "fixed top-14 inset-x-0 bottom-0 bg-black/50 transition-opacity duration-300 opacity-0 pointer-events-none";
        this.overlay.style.zIndex = "60"; // [HIERARCHY] Overlay Layer (Above Header 50, Below Drawer 70)

        // Click to Close Logic (Fixed)
        this.overlay.onclick = (e) => {
            e.stopPropagation();
            this.close();
        };
        // Add Touch Start
        this.overlay.ontouchstart = (e) => {
            // Let it bubble
        };

        // Custom Scrollbar Style (Invisible/Same as BG as requested)
        const style = document.createElement('style');
        style.innerHTML = `
            .drawer-scroll::-webkit-scrollbar { width: 4px; }
            .drawer-scroll::-webkit-scrollbar-track { background: transparent; }
            .drawer-scroll::-webkit-scrollbar-thumb { background-color: transparent; border-radius: 4px; }
            /* Only visible on active scroll/hover if needed, but user asked for "same color" (invisible) */
            .drawer-scroll:hover::-webkit-scrollbar-thumb { background-color: rgba(156, 163, 175, 0.3); } 
        `;
        document.head.appendChild(style);

        // Drawer Panel (Adaptive)
        this.drawer = document.createElement('div');
        this.drawer.className = this.getDrawerClassString();
        this.drawer.style.zIndex = "70"; // [HIERARCHY] Drawer Layer (Above Overlay 60, Below Modals 80)

        // 1. Auth Header (Profile)
        const header = document.createElement('div');
        header.className = "p-6 border-b border-zinc-100 dark:border-zinc-800 relative shrink-0 overflow-hidden whitespace-nowrap";

        // Container for Dynamic Content
        this.headerContent = document.createElement('div');
        header.appendChild(this.headerContent);

        // Add explicit Close Button (Mobile Only)
        const closeBtn = document.createElement('button');
        closeBtn.className = "md:hidden absolute top-4 right-4 p-2 text-zinc-400 hover:text-zinc-600 transition-colors";
        const iconDiv = document.createElement('div');
        renderIcon(iconDiv, 'X', { class: "w-5 h-5" });
        closeBtn.appendChild(iconDiv);
        closeBtn.onclick = () => this.close();
        header.appendChild(closeBtn);

        this.drawer.appendChild(header);

        // 2. Menu Items (Scrollable)
        const scrollArea = document.createElement('div');
        // Removed no-scrollbar, added drawer-scroll
        scrollArea.className = "flex-1 overflow-y-auto overflow-x-hidden py-4 px-3 space-y-1 drawer-scroll";

        this.renderMenuItems(scrollArea); // Sets this.menuContainer
        this.drawer.appendChild(scrollArea);

        // 3. Footer (Logout Only)
        // This section is removed as logout is now part of SIDEBAR_STRUCTURE
        // this.footer = document.createElement('div');
        // this.footer.className = "p-4 border-t border-zinc-100 dark:border-zinc-800 shrink-0 bg-white dark:bg-zinc-900 overflow-hidden whitespace-nowrap";
        // this.drawer.appendChild(this.footer);

        this.container.appendChild(this.overlay);
        this.container.appendChild(this.drawer);

        // Initial Update
        this.updateHeader();
        // this.updateFooter(); // Removed distinct footer
    }

    getDrawerClassString() {
        const isStudio = document.body.classList.contains('is-studio-route');
        // [FIX] Viewer Mode Detection (Episode Viewer)
        // If in Viewer, we want top-0 pt-14 to fill the gap when header hides.
        // If Normal, we want top-14 to respect the Sticky Header.
        const isViewer = window.location.hash.includes('/episode/');

        const topClass = isViewer ? "top-0 pt-14" : "top-14";

        const base = `
            fixed ${topClass} bottom-0 left-0 bg-white dark:bg-zinc-900 shadow-2xl z-[70]
            flex flex-col border-r border-zinc-100 dark:border-zinc-800
            transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
            overflow-hidden group
        `;

        // Logic:
        // 1. Mobile (Classic): Hidden (-x-full) unless isOpen (x-0)
        // 2. Desktop Normal: Always Visible (x-0) via md:translate-x-0. Width varies.
        // 3. Desktop Studio: Hidden (-x-full) unless isOpen (x-0). Behaves like Mobile.

        const transformClass = isStudio
            ? (this.isOpen ? 'translate-x-0' : '-translate-x-full') // Studio: Always toggleable
            : (this.isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'); // Normal: Forced open on Desktop

        const widthClass = "w-[280px]"; // Base width

        // Desktop Logic
        // If Studio: No special desktop width classes (stays w-[280px] when open)
        // If Normal: Apply sidebar expansion logic
        const desktopWidth = isStudio
            ? ""
            : (this.isDesktopExpanded ? "md:w-[280px]" : "md:w-[70px] hover:md:w-[280px]");

        return `${base} ${widthClass} ${transformClass} ${desktopWidth}`;
    }

    updateDrawerState() {
        if (this.drawer) {
            this.drawer.className = this.getDrawerClassString();
        }

        // Handle Overlay (Mobile & Desktop Expanded)
        // User wants "Click empty space to close" on PC too.
        const shouldShowOverlay = this.isOpen || (this.isDesktopExpanded && window.innerWidth >= 768);

        if (shouldShowOverlay) {
            this.overlay.classList.remove('opacity-0', 'pointer-events-none');
            this.overlay.style.pointerEvents = 'auto';
            this.drawer.classList.remove('-translate-x-full'); // Ensure visible

            // Activate Global Click Listener (Safety Net)
            // Use setTimeout to avoid catching the opening click
            setTimeout(() => {
                document.addEventListener('click', this.boundHandleOutsideClick);
            }, 0);
        } else {
            this.overlay.classList.add('opacity-0', 'pointer-events-none');
            this.overlay.style.pointerEvents = 'none';
            if (window.innerWidth < 768) {
                this.drawer.classList.add('-translate-x-full');
            }
            if (window.innerWidth < 768) {
                this.drawer.classList.add('-translate-x-full');
            }
            // Remove Listener - No longer needed with explicit overlay click
            // document.removeEventListener('click', this.boundHandleOutsideClick);
        }
    }

    updateHeader() {
        if (!this.headerContent) return;
        this.headerContent.innerHTML = this.getAuthHeaderHTML();
        this.bindHeaderEvents(this.headerContent);
    }

    // updateFooter() is removed as logout is now part of SIDEBAR_STRUCTURE
    // updateFooter() {
    //     if (!this.footer) return;
    //     this.footer.innerHTML = ''; // Clear

    //     // Wrapper for fade-in content
    //     const contentWrapper = document.createElement('div');
    //     // Visibility logic:
    //     // Mobile: Visible if drawer open (always opacity 1 effectively inside)
    //     // Desktop:
    //     //   If Expanded (Locked): opacity-100
    //     //   If Collapsed: opacity-0 group-hover:opacity-100
    //     const opacityClass = this.isDesktopExpanded
    //         ? "opacity-100"
    //         : "opacity-100 md:opacity-0 md:group-hover:opacity-100";

    //     contentWrapper.className = `flex flex-col gap-2 transition-opacity duration-300 ${opacityClass}`;


    //     // Logout (Conditional) - Prominent Style
    //     if (window.user) {
    //         const logoutBtn = document.createElement('button');
    //         logoutBtn.className = "w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors font-bold text-sm";
    //         logoutBtn.innerHTML = `
    //             <span class="material-symbols-outlined text-lg">logout</span>
    //             <span>로그아웃</span>
    //         `;
    //         logoutBtn.onclick = () => this.handleLogout();
    //         contentWrapper.appendChild(logoutBtn);
    //     }

    //     this.footer.appendChild(contentWrapper);
    // }

    getAuthHeaderHTML() {
        const user = window.user;
        const isStudio = document.body.classList.contains('is-studio-route');
        const opacityClass = this.isDesktopExpanded || isStudio
            ? "opacity-100"
            : "opacity-100 md:opacity-0 md:group-hover:opacity-100";

        if (user) {
            // [FIX] Profile Image Size: w-10 -> w-8 to match header
            return `
                <div id="drawer-user-profile" class="flex items-center gap-3 cursor-pointer p-0 rounded-xl transition-colors min-w-[240px]">
                    <div class="w-8 h-8 min-w-[32px] rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 overflow-hidden border border-zinc-200 dark:border-zinc-700 relative">
                        ${user.photoURL ? `<img src="${user.photoURL}" class="w-full h-full object-cover">` : `<span class="text-sm font-bold">${user.displayName ? user.displayName[0] : 'U'}</span>`}
                    </div>
                    <div class="flex-1 min-w-0 ${opacityClass} transition-opacity duration-300">
                        <div class="font-bold text-gray-900 dark:text-zinc-100 truncate">${user.displayName || '사용자'}</div>
                        <div class="text-xs text-zinc-500 truncate">${user.email}</div>
                    </div>
                </div>
            `;
        } else {
            return `
                <div class="text-center py-2 min-w-[200px] ${opacityClass} transition-opacity duration-300 relative">
                    <h3 class="font-bold text-lg mb-1 dark:text-white">SSULWHA</h3>
                    <p class="text-xs text-zinc-500 mb-3">${locale.t('msg.login_required')}</p>
                    <button id="drawer-login-btn" class="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-2 text-sm font-medium transition-colors shadow-lg shadow-violet-500/30">
                        ${locale.t('msg.login_btn')}
                    </button>
                    <!-- Language Switcher (Guest) -->
                     <button id="drawer-lang-btn" class="absolute top-0 right-0 p-1.5 text-zinc-400 hover:text-violet-500 transition-colors rounded-full hover:bg-violet-50 dark:hover:bg-violet-900/20" title="${locale.t('modal.translate_title')}">
                        <span class="material-symbols-outlined text-xl">language</span>
                    </button>
                </div>
                <!-- Collapsed State Login Icon -->
                <div class="absolute top-6 left-6 md:opacity-100 ${this.isDesktopExpanded ? 'md:opacity-0' : 'md:group-hover:opacity-0'} pointer-events-none transition-opacity">
                     <span class="material-symbols-outlined text-zinc-400">login</span>
                </div>
            `;
        }
    }

    bindHeaderEvents(headerElement) {
        const user = window.user;
        if (user) {
            const profileCard = headerElement.querySelector('#drawer-user-profile');
            if (profileCard) {
                profileCard.onclick = () => {
                    this.close(); // Mobile close
                    // [FIX] Redirect to MyPage as requested
                    window.router.navigate('/mypage');
                };
            }
        } else {
            const loginBtn = headerElement.querySelector('#drawer-login-btn');
            if (loginBtn) {
                loginBtn.onclick = () => {
                    window.router.navigate('/login');
                    this.close();
                };
            }
            const langBtn = headerElement.querySelector('#drawer-lang-btn');
            if (langBtn) {
                langBtn.onclick = async () => {
                    const lang = await modal.showLanguageSelector(locale.getCurrentLanguage());
                    if (lang) {
                        locale.setLanguage(lang);
                    }
                };
            }
        }
    }

    async renderMenuItems(container) {
        this.menuContainer = container;
        this.refreshMenu(); // Initial render (Defaults)
        this.initMenuListener(); // Start listening
    }

    initMenuListener() {
        // [FIX] Use onSnapshot for Real-time Menu Updates
        try {
            const navRef = doc(db, "settings", "navigation");
            // Unsubscribe existing if any
            if (this.unsubscribeMenu) this.unsubscribeMenu();

            this.unsubscribeMenu = onSnapshot(navRef, (snap) => {
                if (snap.exists() && snap.data().sidebar_order) {
                    console.log("[SystemMenuDrawer] Received Menu Update:", snap.data().sidebar_order);
                    this.currentOrder = snap.data().sidebar_order;
                } else {
                    console.warn("[SystemMenuDrawer] No menu data found in 'settings/navigation'. Using defaults.");
                    this.currentOrder = null; // Fallback to default
                }
                this.refreshMenu(); // Re-render immediately
            }, (error) => {
                console.error("[SystemMenuDrawer] Menu Listener Error:", error);
                // console.warn("Menu listener error:", error);
            });
        } catch (e) {
            console.error("Failed to init menu listener", e);
        }
    }

    refreshMenu() {
        if (!this.menuContainer) return;
        this.menuContainer.innerHTML = '';

        const user = window.user;
        let userRole = user ? (user.role || 'user') : 'guest';
        if (user && (user.email === 'hegler.kim@gmail.com' || user.isSuperAdmin)) userRole = 'super_admin';
        if (userRole === 'super_admin') userRole = 'admin';

        // Dynamic Order Logic
        const orderToUse = this.currentOrder || SIDEBAR_STRUCTURE.map(i => {
            if (i.type === 'DIVIDER') return { type: 'DIVIDER' };
            if (i.type === 'EXTERNAL_LINKS_MARKER') return { type: 'EXTERNAL_LINKS_MARKER' };
            return { type: 'ITEM', id: i.id };
        });

        // [FIX] Force-inject 'Recommendation' if missing (for users with custom order)
        const hasRecommendation = orderToUse.some(i => i.id === 'recommendation');
        if (!hasRecommendation) {
            // Find insertion point: After 'wisdom' or before 'guide'
            const wisdomIndex = orderToUse.findIndex(i => i.id === 'wisdom');
            if (wisdomIndex !== -1) {
                orderToUse.splice(wisdomIndex + 1, 0, { type: 'ITEM', id: 'recommendation' });
            } else {
                // Fallback: Add before Guide
                const guideIndex = orderToUse.findIndex(i => i.id === 'guide');
                if (guideIndex !== -1) {
                    orderToUse.splice(guideIndex, 0, { type: 'ITEM', id: 'recommendation' });
                }
            }
        }

        const itemMap = new Map();
        SIDEBAR_STRUCTURE.forEach(item => {
            if (item.id) itemMap.set(item.id, item);
        });

        orderToUse.forEach(entry => {
            let item = null;
            if (entry.type === 'ITEM') {
                const baseItem = itemMap.get(entry.id);
                if (baseItem) {
                    // Clone to avoid mutating the master structure
                    item = { ...baseItem };
                    // Apply Custom Label if exists (and not empty)
                    if (entry.customLabel) {
                        item.label = entry.customLabel;
                        item.customLabel = entry.customLabel; // [FIX] Preserve for Priority Logic
                    } else {
                        // [FIX] Priority Translation (Upstream)
                        // Translate label immediately so it persists for Modal overrides (handleMenuClick)
                        // This prevents the raw Korean label from navigation.js being passed to InfoModal
                        if (item.id) {
                            const key = `menu.${item.id}`;
                            const translated = locale.t(key);
                            if (translated !== key) {
                                item.label = translated;
                            }
                        }
                    }
                }
            }
            else if (entry.type === 'DIVIDER') item = { type: 'DIVIDER' };
            else if (entry.type === 'EXTERNAL_LINKS_MARKER') item = { type: 'EXTERNAL_LINKS_MARKER' };
            // [NEW] Dynamic Modal Item support
            else if (entry.type === 'MODAL' && entry.docId) {
                item = {
                    type: 'MODAL',
                    // [FIX] FORCE InfoModal (Cosmic) to upgrade existing legacy items (MarkdownModal)
                    component: 'InfoModal',
                    docId: entry.docId,
                    label: entry.customLabel || '문서',
                    icon: 'FileText',
                    id: entry.id || `doc-${entry.docId}`
                };
            }

            if (!item) return;

            // [FIX] Universal Override: If it's a Document, FORCE Cosmic Theme (InfoModal)
            // This catches legacy items, static items, and manually added items.
            if (item.docId) {
                item.component = 'InfoModal';
                if (!item.icon || item.icon === 'article') item.icon = 'FileText';
            }

            if (!item) return;
            // Role Check
            if (item.role && item.role !== userRole && userRole !== 'admin') return;
            // Also hide "Logout" if not logged in
            if (item.id === 'logout' && !user) return;


            if (item.type === 'DIVIDER') {
                const divider = document.createElement('div');
                divider.className = "my-2 border-t border-zinc-100 dark:border-zinc-800 mx-2";
                this.menuContainer.appendChild(divider);
                return;
            }

            if (item.type === 'EXTERNAL_LINKS_MARKER') {
                // Render External Links Container
                const extContainer = document.createElement('div');
                extContainer.className = `border-t border-zinc-100 dark:border-zinc-800 mt-2 pt-2 space-y-1 mx-2 transition-opacity duration-300 delay-100 ${this.isDesktopExpanded ? "opacity-100" : "opacity-100 md:opacity-0 md:group-hover:opacity-100"}`;
                this.menuContainer.appendChild(extContainer);
                this.loadExternalLinks(extContainer);
                return;
            }

            // Normal Item
            const isActive = item.path === this.activePath || (item.path !== '/' && this.activePath.startsWith(item.path));
            const el = this.createMenuItemElement(item, isActive);
            this.menuContainer.appendChild(el);
        });
    }

    createMenuItemElement(item, isActive) {
        const btn = document.createElement('button');
        const isStudio = document.body.classList.contains('is-studio-route');
        const opacityClass = this.isDesktopExpanded || isStudio
            ? "opacity-100"
            : "opacity-100 md:opacity-0 md:group-hover:opacity-100";

        // Active State Styling
        // Explicit justify-start and text-left to prevent centering
        let baseClass = "w-full flex items-center justify-start text-left gap-4 px-3 py-3 rounded-xl transition-colors mb-1 whitespace-nowrap overflow-hidden ";

        if (isActive) {
            baseClass += "bg-violet-50 text-violet-900 dark:bg-violet-900/20 dark:text-violet-100 font-bold ";
        } else if (item.highlight) {
            // Highlighted items (Admin, Studio, Plaza) -> Purple Text
            baseClass += "text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 font-medium ";
        } else {
            baseClass += "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 ";
        }

        // Special styling for Logout (Left Aligned, Red)
        if (item.id === 'logout') {
            baseClass = "w-full flex items-center justify-center text-center gap-2 px-3 py-3 rounded-xl transition-colors mb-1 whitespace-nowrap overflow-hidden bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 font-bold ";
        }

        btn.className = baseClass;

        // Icon
        const iconDiv = document.createElement('div');
        iconDiv.className = "shrink-0 transition-all duration-300 ";

        // Apply Glow if Active
        if (isActive) {
            iconDiv.className += "icon-glow ";
        }

        renderIcon(iconDiv, item.icon, { class: "w-6 h-6" });
        btn.appendChild(iconDiv);

        // Label
        const label = document.createElement('div');
        label.className = `flex flex-col items-start min-w-0 flex-1 ${opacityClass} transition-opacity duration-200 delay-75`;

        const mainLabel = document.createElement('span');
        // If active, bold. If highlight, medium?
        // Increased font size: text-base (was text-sm)
        mainLabel.className = "text-base truncate w-full " + (isActive ? "font-bold" : (item.highlight ? "font-semibold" : "font-medium"));

        // [LOCALE] Try to translate menu item
        // e.g. menu.home, menu.webtoon
        let labelText = item.label;
        // [FIX] Priority Inversion: Translation < Custom Label
        // Only attempt translation if there is NO custom label override.
        if (item.id && !item.customLabel) {
            const key = `menu.${item.id}`;
            const translated = locale.t(key);
            if (translated !== key) {
                labelText = translated;
            }
        }
        mainLabel.textContent = labelText;
        label.appendChild(mainLabel);

        if (item.subheading) {
            const sub = document.createElement('span');
            // Subheading color should be lighter?
            sub.className = "text-[10px] truncate w-full " + (isActive || item.highlight ? "text-violet-400/80" : "text-zinc-400");
            sub.textContent = item.subheading;
            label.appendChild(sub);
        }

        btn.appendChild(label);

        btn.onclick = () => {
            // Mobile Close Logic:
            // User REQ: On Mobile, ALWAYS close the menu when clicking an item (even Modals)
            // so the modal/content is visible.
            if (window.innerWidth <= 768) {
                this.close();
            }

            this.handleMenuClick(item);

            // For Path navigation, manually update active state fallback
            if (item.path) {
                this.activePath = item.path;
                this.refreshMenu();
            }
        };
        return btn;
    }

    async loadExternalLinks(container) {
        try {
            const snap = await getDoc(doc(db, 'settings', 'config'));
            if (snap.exists() && snap.data().external_links && snap.data().external_links.length > 0) {
                const links = snap.data().external_links;
                container.innerHTML = '';
                // container.style.display = 'block'; // Handled by parent container's display

                const isStudio = document.body.classList.contains('is-studio-route');
                const opacityClass = this.isDesktopExpanded || isStudio
                    ? "opacity-100"
                    : "opacity-100 md:opacity-0 md:group-hover:opacity-100";

                links.forEach(link => {
                    const btn = document.createElement('a');
                    btn.href = link.url;
                    btn.target = "_blank";
                    btn.className = "w-full flex items-center justify-start gap-4 px-3 py-3 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-xl transition-colors mb-1 no-underline whitespace-nowrap overflow-hidden";
                    btn.innerHTML = `
                        <span class="material-symbols-outlined text-xl shrink-0">${link.icon || 'link'}</span>
                        <div class="flex flex-col items-start min-w-0 ${opacityClass}">
                             <span class="font-medium text-sm text-zinc-800 dark:text-zinc-200">${link.label}</span>
                             <span class="text-[10px] text-zinc-400">외부 링크</span>
                        </div>
                    `;
                    container.appendChild(btn);
                });
            }
        } catch (e) {
            console.warn("Failed to load dynamic links", e);
        }
    }

    handleMenuClick(item) {
        if (item.path) {
            if (item.type === 'LINK') {
                window.open(item.path, '_blank');
            } else {
                window.router.navigate(item.path);
            }
        } else if (item.type === 'MODAL') {
            try {
                switch (item.component) {
                    case 'InfoModal':
                        if (InfoModal && item.docId) ModalManager.show(InfoModal, item.docId, item.label);
                        break;
                    case 'NoticeModal':
                        if (GlobalNoticeModal) ModalManager.show(GlobalNoticeModal);
                        break;
                    case 'MarkdownModal':
                        if (MarkdownModal && item.docId) ModalManager.show(MarkdownModal, item.docId, item.label);
                        break;
                    case 'ReportModal':
                        if (ReportModal) ModalManager.show(ReportModal);
                        break;
                    case 'ProposalModal':
                        ModalManager.show(ProposalModal); // Using Manager
                        break;
                    case 'GuideModal':
                        ModalManager.show(GuideModal); // Using Manager
                        break;
                    default:
                        console.warn("Unknown Modal Component:", item.component);
                }
            } catch (e) {
                console.error("Failed to load modal:", e);
                alert("기능을 불러오는데 실패했습니다.");
            }
        } else if (item.type === 'FUNCTION') {
            if (item.action === 'shareLink') {
                if (navigator.share) {
                    navigator.share({ title: 'SSULWHA', text: '썰화 - 웹툰/웹소설 플랫폼', url: window.location.href });
                } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('링크가 복사되었습니다!');
                }
            } else if (item.action === 'logout') {
                this.handleLogout();
            }
        }
    }

    handleLogout() {
        logout().then(() => {
            window.location.reload();
        });
    }

    open() {
        // Mobile Only Logic
        this.isOpen = true;
        this.updateDrawerState();

        if (this.refreshMenu) this.refreshMenu();
        if (this.updateHeader) this.updateHeader();
        // if (this.updateFooter) this.updateFooter(); // Removed
    }

    close() {
        this.isOpen = false;
        this.updateDrawerState();

        // Also collapse desktop if needed? 
        // No, desktop collapse is handled by toggleDesktop.
        // But if close() is called explicitly, maybe we should collapse?
        // Let's safe-guard:
        if (window.innerWidth >= 768 && this.isDesktopExpanded) {
            this.isDesktopExpanded = false;
            this.updateDrawerState();
            this.updateHeader();
            // this.updateFooter(); // Removed
            this.refreshMenu(); // To update text opacity
        }
    }

    toggle() {
        // [FIX] Studio Mode Logic
        // In Studio, even on Desktop, it behaves like Mobile Overlay.
        const isStudio = document.body.classList.contains('is-studio-route');

        if (window.innerWidth >= 768 && !isStudio) {
            // Desktop Toggle (Normal Sidebar Expansion)
            this.isDesktopExpanded = !this.isDesktopExpanded;
            this.updateDrawerState();
            // Need to re-render header/footer/menu to update opacity classes
            this.updateHeader();
            // this.updateFooter(); // Removed
            this.refreshMenu();
        } else {
            // Mobile Toggle OR Studio Desktop Toggle (Overlay)
            this.isOpen ? this.close() : this.open();
        }
    }
}
